import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import Row from './Row';
import { SectionState as SectionDataType, RowState as RowDataType, BarState, ChordObjectModelV3, TimeSignature } from '../types';
import { KeySignature, getSectionNote, getSectionRepeat } from '@chordbaker/music-theory';
import TrashIcon from './icons/TrashIcon';
import DuplicateIcon from './icons/DuplicateIcon';
import PlusIcon from './icons/PlusIcon';
import RepeatIcon from './icons/RepeatIcon';
import NoteIcon from './icons/NoteIcon';
import SectionRepeatPill from './SectionRepeatPill';
import SectionRepeatEditorModal from './SectionRepeatEditorModal';
import SectionNoteEditorModal from './SectionNoteEditorModal';
import { useTheme, Theme } from '../contexts/ThemeContext';

// RN-ALIGNED: Clean createStyles function matching React Native pattern
const createStyles = (theme: Theme) => ({
  sectionContainer: {
    backgroundColor: 'transparent',
    paddingLeft: theme.spacing.lg,
    paddingRight: theme.spacing.lg,
    // Sections are separated by the previous section's reserved row action bar
    // plus this small vertical padding
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    borderRadius: theme.radii.lg,
    marginBottom: 0,
    // WEB-SPECIFIC: Use CSS transition instead of RN Animated
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  headerContainer: {
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    marginBottom: theme.spacing.sm, // the first row adds its own tappable strip above the slots
    minHeight: theme.sizes.hitTarget,
  },
  // Sized to its text (not flex: 1) so the repeat pill and the add buttons
  // sit right after the title; the spacer after them still focuses the title.
  titleInput: {
    flex: '0 1 auto',
    minWidth: 0,
    fontSize: theme.typography.sizes.xxxl,
    fontWeight: theme.typography.weights.light,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    backgroundColor: 'transparent',
    color: theme.colors.text,
    height: theme.sizes.hitTarget,
    border: 'none',
    outline: 'none',
  },
  // Invisible copy of the title (or placeholder) that the input's width follows
  titleMeasure: {
    position: 'absolute' as const,
    visibility: 'hidden' as const,
    whiteSpace: 'pre' as const,
    fontSize: theme.typography.sizes.xxxl,
    fontWeight: theme.typography.weights.light,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
  },
  headerSpacer: {
    flex: 1,
    alignSelf: 'stretch',
    cursor: 'text',
  },
  // Dashed "add repeat" / "add note" buttons, shown only while the section is
  // active — the same dashed-outline language as the time signature's add button
  addButtons: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: theme.spacing.sm,
    flexShrink: 0,
  },
  addButtonsAfterPill: {
    marginLeft: theme.spacing.sm,
  },
  addButton: {
    width: theme.spacing.xxl,
    height: theme.sizes.miniButton,
    padding: 0,
    border: `1px dashed ${theme.colors.border}`,
    borderRadius: theme.radii.full,
    backgroundColor: 'transparent',
    color: theme.colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  // The section note: its own line under the title, wrapping
  noteButton: {
    display: 'block',
    marginTop: -theme.spacing.sm, // tuck under the title: the header row is taller than its text
    marginBottom: theme.spacing.sm,
    padding: 0,
    border: 'none',
    background: 'none',
    textAlign: 'left' as const,
    fontSize: theme.typography.sizes.md,
    lineHeight: theme.typography.lineHeights.normal,
    fontStyle: 'italic',
    color: theme.colors.textTertiary,
    whiteSpace: 'pre-wrap' as const,
    overflowWrap: 'anywhere' as const,
    cursor: 'pointer',
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: theme.spacing.xxs,
  },
  // Flat neutral icon buttons: the editing chrome shouldn't compete with the
  // chart's own colors (green = degrees, blue = tones). Only delete is colored.
  actionButton: {
    marginLeft: theme.spacing.xs,
    padding: theme.spacing.sm,
    borderRadius: theme.radii.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: theme.colors.surfaceSecondary,
    // WEB-SPECIFIC: CSS transition instead of RN activeOpacity
    transition: `all ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  disabledButton: {
    opacity: theme.opacity.disabled,
    cursor: 'not-allowed',
  },
  rowsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    // No extra gap: each row already reserves its action bar below the slots
    gap: 0,
  },
});

export interface SectionProps {
  sectionData: SectionDataType;
  onUpdateSectionTitle: (sectionId: string, newTitle: string) => void;
  // Slot actions
  onAddSlotInSection: (afterSlotId: string, rowId: string, sectionId: string) => void;
  onAddSlotBeforeInSection: (beforeSlotId: string, rowId: string, sectionId: string) => void;
  onDeleteSlotInSection: (slotIdToDelete: string, rowId: string, sectionId: string) => void;
  onUpdateChordInSection: (slotId: string, newChord: ChordObjectModelV3 | null, rowId: string, sectionId: string, customText?: string) => void;
  onUpdateLyricsInSection?: (slotId: string, lyrics: string, rowId: string, sectionId: string) => void;
  // Row actions
  onAddRowInSection: (afterRowId: string, sectionId: string) => void;
  onDuplicateRowInSection: (rowIdToDuplicate: string, sectionId: string) => void;
  onDeleteRowInSection: (rowIdToDelete: string, sectionId: string) => void;
  onAddSectionAfterRowInSection: (currentRowId: string, sectionId: string) => void;
  onSetRowTimeSignatureInSection: (rowId: string, sectionId: string, timeSignature: TimeSignature | null) => void;
  /** The song has a global time signature, which replaces the per-row ones. */
  hasGlobalTimeSignature?: boolean;
  /** Each row's meter by row id (`getRowMeters`); rows missing from it play in 4/4. */
  rowMeters?: Map<string, TimeSignature>;
  showBeats?: boolean;
  onApplyBarInSection?: (rowId: string, barId: string, bar: BarState, focusSlotId: string, sectionId: string) => void;
  onCantAddInsideInSection?: (slotId: string, rowId: string, sectionId: string) => void;
  /** Set when the song has no time signature (the chord editor's Beats shortcut asks for one). */
  onRequestSetTimeSignature?: () => void;
  /** A just-added slot whose chord editor should open, and the reset after it does. */
  slotToOpenChordEditor?: string | null;
  onChordEditorOpened?: () => void;
  // Section repeat count ("×2") and note; null clears
  onSetSectionRepeat: (sectionId: string, repeat: number | null) => void;
  onSetSectionNote: (sectionId: string, note: string | null) => void;
  // Section actions
  onAddSectionAfterSection: (sectionId: string) => void;
  onDeleteSection: (sectionIdToDelete: string) => void;
  isDeleteDisabled?: boolean;
  onDuplicateSection: (sectionIdToDuplicate: string) => void;
  showNotes?: boolean;
  showDegrees?: boolean;
  showLyrics?: boolean;
  selectedKey?: KeySignature | null;
  // New props for managing active element state
  activeSectionId?: string | null;
  onSectionPress: (sectionId: string) => void;
  // WEB-SPECIFIC: activates without toggling, for the title input and header spacer
  onActivateSection: (sectionId: string) => void;
  onClearActiveSection: () => void;
  // Row-related active state props
  activeRowId?: string | null;
  onRowPress: (rowId: string) => void;
  onClearActiveRow: () => void;
  // Slot-related active state props
  activeSlotId?: string | null;
  onSlotPress: (slotId: string) => void;
  onClearActiveSlot: () => void;
  // Centralized control visibility function
  getControlVisibility: (elementId: string, elementType: 'section' | 'row' | 'slot') => boolean;
}

const Section: React.FC<SectionProps> = ({
  sectionData,
  onUpdateSectionTitle,
  onAddSlotInSection,
  onAddSlotBeforeInSection,
  onDeleteSlotInSection,
  onUpdateChordInSection,
  onUpdateLyricsInSection,
  onAddRowInSection,
  onDuplicateRowInSection,
  onDeleteRowInSection,
  onAddSectionAfterRowInSection,
  onSetRowTimeSignatureInSection,
  hasGlobalTimeSignature = false,
  rowMeters,
  showBeats = false,
  onApplyBarInSection,
  onCantAddInsideInSection,
  onRequestSetTimeSignature,
  slotToOpenChordEditor,
  onChordEditorOpened,
  onSetSectionRepeat,
  onSetSectionNote,
  onAddSectionAfterSection,
  onDeleteSection,
  isDeleteDisabled,
  onDuplicateSection,
  showNotes,
  showDegrees,
  showLyrics,
  selectedKey,
  activeSectionId,
  onSectionPress,
  onActivateSection,
  onClearActiveSection,
  activeRowId,
  onRowPress,
  onClearActiveRow,
  activeSlotId,
  onSlotPress,
  onClearActiveSlot,
  getControlVisibility,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleResize = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(event.matches);
    };

    handleResize(mediaQuery);
    mediaQuery.addEventListener('change', handleResize);

    return () => {
      mediaQuery.removeEventListener('change', handleResize);
    };
  }, []);

  const style = styles.sectionContainer;

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onUpdateSectionTitle(sectionData.id, event.target.value);
  };

  const handleSectionPress = () => {
    onSectionPress(sectionData.id);
  };

  // Any focus, keyboard (Tab) included, shows the controls without toggling
  const handleTextInputFocus = () => {
    onActivateSection(sectionData.id);
  };

  const isActive = getControlVisibility(sectionData.id, 'section');
  const placeholder = isMobile ? "Section name" : "Section name (e.g. Verse, Chorus, Bridge)";

  const repeat = getSectionRepeat(sectionData);
  const note = getSectionNote(sectionData);
  const showAddButtons = isActive && (repeat === null || note === null);
  const [editor, setEditor] = useState<'repeat' | 'note' | null>(null);

  // The title input is as wide as its text; the note lines up with that text
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [titleWidth, setTitleWidth] = useState<number | null>(null);
  const [titleX, setTitleX] = useState(0);
  useLayoutEffect(() => {
    if (measureRef.current) {
      // +xxs leaves room for the caret after the last character
      setTitleWidth(Math.ceil(measureRef.current.getBoundingClientRect().width) + theme.spacing.xxs);
    }
    if (inputRef.current) {
      setTitleX(inputRef.current.offsetLeft);
    }
  }, [sectionData.title, placeholder, theme]);

  // A click on the title or the empty header after it only ever shows the
  // controls. It must not bubble to handleSectionPress: mousedown already
  // focused the input, and a second toggle would hide them again.
  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    inputRef.current?.focus();
    onActivateSection(sectionData.id);
  };

  const openEditor = (which: 'repeat' | 'note') => (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditor(which);
  };

  return (
    <div style={style}>
      {/* WEB-SPECIFIC: Global placeholder styling (RN uses placeholderTextColor prop) */}
      <style>
        {`
          input::placeholder {
            color: ${theme.colors.textTertiary};
            opacity: 1;
          }
        `}
      </style>

      <div
        onClick={handleSectionPress}
      >
        <div style={styles.headerContainer}>
          <span ref={measureRef} aria-hidden="true" style={styles.titleMeasure}>
            {sectionData.title || placeholder}
          </span>
          <input
            ref={inputRef}
            type="text"
            value={sectionData.title}
            onChange={handleTitleChange}
            onFocus={handleTextInputFocus}
            onClick={handleTitleClick}
            placeholder={placeholder}
            style={{ ...styles.titleInput, ...(titleWidth !== null ? { width: titleWidth } : { flex: 1 }) }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
          />

          {repeat !== null && (
            <SectionRepeatPill repeat={repeat} onClick={openEditor('repeat')} />
          )}

          {/* Add repeat / add note — only while active, only for what's unset */}
          {showAddButtons && (
            <div style={{ ...styles.addButtons, ...(repeat !== null ? styles.addButtonsAfterPill : {}) }}>
              {repeat === null && (
                <button
                  type="button"
                  onClick={openEditor('repeat')}
                  style={styles.addButton}
                  aria-label="Add repeat"
                  title="Add repeat"
                >
                  <RepeatIcon size={theme.sizes.icon.sm} />
                </button>
              )}
              {note === null && (
                <button
                  type="button"
                  onClick={openEditor('note')}
                  style={styles.addButton}
                  aria-label="Add note"
                  title="Add note"
                >
                  <NoteIcon size={theme.sizes.icon.sm} />
                </button>
              )}
            </div>
          )}

          {/* The rest of the header still focuses the title, as before */}
          <div style={styles.headerSpacer} onClick={handleTitleClick} data-testid="section-header-spacer" />

          {/* Action buttons - only show when section is active */}
          {isActive && (
            <div style={styles.actionsContainer}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClearActiveRow();
                  onClearActiveSlot();
                  onAddSectionAfterSection(sectionData.id);
                }}
                style={styles.actionButton}
                title="Add section"
              >
                <PlusIcon size={theme.sizes.icon.sm} color={theme.colors.textSecondary} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClearActiveRow();
                  onClearActiveSlot();
                  onDuplicateSection(sectionData.id);
                }}
                style={styles.actionButton}
                title="Duplicate section"
              >
                <DuplicateIcon
                  size={theme.sizes.icon.sm}
                  color={theme.colors.textSecondary}
                />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClearActiveRow();
                  onClearActiveSlot();
                  onDeleteSection(sectionData.id);
                }}
                disabled={isDeleteDisabled}
                style={{
                  ...styles.actionButton,
                  ...(isDeleteDisabled ? styles.disabledButton : {}),
                }}
                title={isDeleteDisabled ? "Cannot delete the last section" : "Delete section"}
              >
                <TrashIcon
                  size={theme.sizes.icon.sm}
                  color={isDeleteDisabled ? theme.colors.textTertiary : theme.colors.error}
                />
              </button>
            </div>
          )}
        </div>

        {note !== null && (
          <button
            type="button"
            onClick={openEditor('note')}
            style={{ ...styles.noteButton, marginLeft: titleX + theme.spacing.sm, width: `calc(100% - ${titleX + theme.spacing.sm}px)` }}
            title="Edit section note"
            data-testid="section-note"
          >
            {note}
          </button>
        )}

        <div style={styles.rowsContainer}>
          {sectionData.rows.map((rowData: RowDataType) => (
            <Row
              key={rowData.id}
              row={rowData}
              sectionId={sectionData.id}
              onAddSlot={(rowId, afterSlotId) => {
                onSlotPress(afterSlotId);
                onAddSlotInSection(afterSlotId, rowId, sectionData.id);
              }}
              onAddSlotBefore={(rowId, beforeSlotId) => {
                onSlotPress(beforeSlotId);
                onAddSlotBeforeInSection(beforeSlotId, rowId, sectionData.id);
              }}
              onDeleteSlot={(rowId, slotIdToDelete) => {
                onClearActiveSection();
                onClearActiveRow();
                onClearActiveSlot();
                onDeleteSlotInSection(slotIdToDelete, rowId, sectionData.id);
              }}
              onUpdateChord={(rowId, slotId, newChord, customText) => {
                onSlotPress(slotId);
                onUpdateChordInSection(slotId, newChord, rowId, sectionData.id, customText);
              }}
              onUpdateLyrics={(rowId, slotId, lyrics) => {
                if (onUpdateLyricsInSection) {
                  onUpdateLyricsInSection(slotId, lyrics, rowId, sectionData.id);
                }
              }}
              onAddRowAfter={(currentRowId) => {
                onRowPress(currentRowId);
                onAddRowInSection(currentRowId, sectionData.id);
              }}
              onDuplicateRow={(rowIdToDuplicate) => {
                onRowPress(rowIdToDuplicate);
                onDuplicateRowInSection(rowIdToDuplicate, sectionData.id);
              }}
              onDeleteRow={(rowIdToDelete) => {
                onClearActiveSection();
                onClearActiveRow();
                onClearActiveSlot();
                onDeleteRowInSection(rowIdToDelete, sectionData.id);
              }}
              canDeleteRow={sectionData.rows.length > 1}
              onAddSectionAfterRow={(currentRowId) => {
                onSectionPress(sectionData.id);
                onAddSectionAfterRowInSection(currentRowId, sectionData.id);
              }}
              hasGlobalTimeSignature={hasGlobalTimeSignature}
              meter={rowMeters?.get(rowData.id)}
              showBeats={showBeats}
              onRequestSetTimeSignature={onRequestSetTimeSignature}
              slotToOpenChordEditor={slotToOpenChordEditor}
              onChordEditorOpened={onChordEditorOpened}
              onApplyBar={(rowId, barId, bar, focusSlotId) => {
                onApplyBarInSection?.(rowId, barId, bar, focusSlotId, sectionData.id);
              }}
              onCantAddInside={(rowId, slotId) => {
                onCantAddInsideInSection?.(slotId, rowId, sectionData.id);
              }}
              onSetRowTimeSignature={(rowId, timeSignature) => {
                onRowPress(rowId);
                onSetRowTimeSignatureInSection(rowId, sectionData.id, timeSignature);
              }}
              showNotes={showNotes}
              showDegrees={showDegrees}
              showLyrics={showLyrics}
              selectedKey={selectedKey}
              activeRowId={activeRowId}
              onRowPress={onRowPress}
              onClearActiveRow={onClearActiveRow}
              activeSlotId={activeSlotId}
              onSlotPress={onSlotPress}
              onClearActiveSlot={onClearActiveSlot}
              getControlVisibility={getControlVisibility}
            />
          ))}
        </div>
      </div>

      <SectionRepeatEditorModal
        isOpen={editor === 'repeat'}
        sectionTitle={sectionData.title}
        initialValue={repeat}
        onClose={() => setEditor(null)}
        onApply={value => {
          setEditor(null);
          onSetSectionRepeat(sectionData.id, value);
        }}
      />
      <SectionNoteEditorModal
        isOpen={editor === 'note'}
        sectionTitle={sectionData.title}
        initialValue={note}
        onClose={() => setEditor(null)}
        onApply={value => {
          setEditor(null);
          onSetSectionNote(sectionData.id, value);
        }}
      />
    </div>
  );
};

export default Section;
